import { DateTime, type DateTimeFormatOptions } from 'luxon'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function firestoreTimestampToDate(timestamp: any) {
  if (timestamp && timestamp.seconds) {
    return new Date(timestamp.seconds * 1000)
  }
  if (timestamp && typeof timestamp === 'string') {
    return new Date(timestamp)
  }
  if (timestamp && typeof timestamp === 'number') {
    return new Date(timestamp)
  }
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    return new Date(timestamp.toDate())
  }
  if (timestamp instanceof Date) {
    return timestamp
  }
  return timestamp
}

/**
 * Convert seconds to minutes, safely handling null/undefined values
 */
export function secondsToMinutes(seconds: number | null | undefined): number {
  return Math.round((seconds || 0) / 60)
}

/**
 * Format duration in minutes to a readable format
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`
  } else {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60

    if (remainingMinutes === 0) {
      return `${hours}h`
    } else {
      return `${hours}h ${remainingMinutes}m`
    }
  }
}

export function toTestId(text: string) {
  return text.toLowerCase().replace(/[^a-zA-Z0-9]+/g, '-')
}

export const formatSessionDate = (date: string | Date | null | undefined) => {
  if (!date) return 'N/A'

  const dateTime = DateTime.fromJSDate(new Date(date))
    .setZone(DateTime.now().zoneName)
    .setLocale(DateTime.now().locale)

  const dayAndDate = dateTime.toLocaleString({
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })

  const timeWithTimezone = dateTime.toLocaleString({
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  })

  return `${dayAndDate} at ${timeWithTimezone}`
}

export const formatTime = (
  date: Date,
  options?: { withTimezone?: boolean; format?: DateTimeFormatOptions },
) => {
  const { format = DateTime.TIME_SIMPLE } = options || {}
  const { withTimezone = false } = options || {}

  const dateTime = DateTime.fromJSDate(new Date(date))
    .setZone(DateTime.now().zoneName)
    .setLocale(DateTime.now().locale)
  const formattedTime = dateTime.toLocaleString(format)

  if (withTimezone) {
    const offsetNameShort = dateTime.offsetNameShort
    return `${formattedTime} ${offsetNameShort}`
  }
  return formattedTime
}

export const toLocaleString = (
  date: Date,
  options: { withTimezone: boolean } = { withTimezone: false },
) => {
  const { withTimezone } = options
  const dateTime = DateTime.fromJSDate(new Date(date))
    .setZone(DateTime.now().zoneName)
    .setLocale(DateTime.now().locale)

  const formattedDate = dateTime.toJSDate().toLocaleString()

  if (withTimezone) {
    const offsetNameShort = dateTime.offsetNameShort
    return `${formattedDate} ${offsetNameShort}`
  }
  return formattedDate
}
